/**
 * Plan & Feature Gating Type Definitions
 * Defines the tier-based feature access system
 */

import { PlanTier, SubscriptionStatus } from './billing.types';

/**
 * Every gatable feature in the app.
 * Add new features here — TypeScript will enforce registry completeness.
 */
export type PlanFeature =
  | 'ai_dictation'
  | 'standard_templates'
  | 'advanced_templates'
  | 'user_custom_templates'
  | 'user_custom_question_schemas'
  | 'ask_charti'
  | 'priority_support'
  | 'usage_analytics'
  | 'emr_integration'
  | 'api_access';

/** How a feature is gated */
export type GateType = 'boolean' | 'quota';

/** Configuration for a single feature on a single tier */
export interface FeatureEntitlement {
  enabled: boolean;
  gateType: GateType;
  /** For quota-based features: max allowed per period. undefined = unlimited */
  limit?: number;
  /** Period for quota reset */
  period?: 'daily' | 'monthly';
}

/** The full registry: feature -> tier -> entitlement */
export type FeatureRegistry = Record<PlanFeature, Record<PlanTier, FeatureEntitlement>>;

/** Runtime feature check result returned by usePlanFeature() */
export interface FeatureAccess {
  allowed: boolean;
  gateType: GateType;
  limit?: number;
  usage?: number;
  remaining?: number;
  /** True if this is a soft limit that's been exceeded */
  exceeded?: boolean;
}

/** Usage data fetched from backend for quota-based features */
export interface PlanUsage {
  ask_charti_count?: number;
  [key: string]: number | undefined;
}

/** Full plan state exposed by PlanContext */
export interface PlanState {
  tier: PlanTier;
  status: SubscriptionStatus;
  isLoading: boolean;
  usage: PlanUsage;
  /** Check if a feature is accessible for the current plan */
  checkFeature: (feature: PlanFeature) => FeatureAccess;
  /** Force refresh plan and usage data from API */
  refreshPlan: () => Promise<void>;
}

/**
 * Maps PlanFeature to the usage field key in PlanUsage.
 * Only quota-based features need a mapping here.
 */
export const FEATURE_USAGE_KEYS: Partial<Record<PlanFeature, keyof PlanUsage>> = {
  ask_charti: 'ask_charti_count',
};

/**
 * Human-readable labels for features (used in UpgradePrompt)
 */
export const FEATURE_LABELS: Record<PlanFeature, string> = {
  ai_dictation: 'AI Dictation',
  standard_templates: 'Standard Templates',
  advanced_templates: 'Advanced Templates',
  user_custom_templates: 'Custom Templates',
  user_custom_question_schemas: 'Custom Question Schemas',
  ask_charti: 'Ask Charti',
  priority_support: 'Priority Support',
  usage_analytics: 'Usage Analytics',
  emr_integration: 'EMR Integration',
  api_access: 'API Access',
};

/**
 * The minimum tier required for each feature (used in UpgradePrompt messaging)
 */
export const FEATURE_MIN_TIER: Record<PlanFeature, PlanTier> = {
  ai_dictation: 'free',
  standard_templates: 'free',
  advanced_templates: 'pro',
  user_custom_templates: 'pro',
  user_custom_question_schemas: 'free', // available on free but capped at 1
  ask_charti: 'free', // available on free but quota-limited
  priority_support: 'pro',
  usage_analytics: 'pro',
  emr_integration: 'enterprise',
  api_access: 'enterprise',
};
